import type { GameFlow, GameFlowSnapshot } from "./game-flow";
import type { PlayerRuntime } from "./player-runtime";
import type { ProgressPreferences, ProgressState } from "./progress-state";
import type { ProgressMutation } from "./progress-mutation";
import {
  isSelectedSoundtrackId,
  type SelectedSoundtrackId,
} from "../config/soundtrack-config";

export interface PageVisibilitySource {
  readonly hidden: boolean;
  addEventListener(type: "visibilitychange", listener: () => void): void;
  removeEventListener(type: "visibilitychange", listener: () => void): void;
}

export interface GameplayLifecycleSnapshot {
  readonly mode: GameFlowSnapshot["mode"];
  readonly paused: boolean;
  readonly platformPaused: boolean;
  readonly preferences: ProgressPreferences;
  readonly progressPersistence: "durable" | "failed" | "pending";
  readonly scene: GameFlowSnapshot["scene"];
  readonly selectedSoundtrackId: SelectedSoundtrackId;
}

export type GameplayLifecycleListener = (
  snapshot: GameplayLifecycleSnapshot,
) => void;

export interface GameplayProgressStore {
  getState(): ProgressState;
  update(
    createNextState: (state: ProgressState) => ProgressState,
  ): ProgressMutation<boolean>;
}

export interface GameplayExitGuard {
  prepareForExit(): boolean;
}

export class GameplayLifecycle {
  private destroyed = false;
  private readonly listeners = new Set<GameplayLifecycleListener>();
  private nativeActive = true;
  private progressPersistence: GameplayLifecycleSnapshot["progressPersistence"] =
    "durable";
  private readonly unsubscribeFlow: () => void;

  public constructor(
    private readonly flow: GameFlow,
    private readonly runtime: PlayerRuntime,
    private readonly progressStore: GameplayProgressStore,
    private readonly visibilitySource: PageVisibilitySource,
    private readonly exitGuard?: GameplayExitGuard,
  ) {
    visibilitySource.addEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    this.unsubscribeFlow = flow.subscribe(() => {
      this.runtime.resume();
      this.syncPlatformPause();
      this.notify();
    });
  }

  public destroy(): void {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    this.visibilitySource.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    this.unsubscribeFlow();
    this.listeners.clear();
  }

  public getSnapshot(): GameplayLifecycleSnapshot {
    const flowSnapshot = this.flow.getSnapshot();
    const progress = this.progressStore.getState();
    return {
      mode: flowSnapshot.mode,
      paused: this.runtime.getSnapshot().paused,
      platformPaused: this.runtime.getSnapshot().backgrounded,
      preferences: { ...progress.preferences },
      progressPersistence: this.progressPersistence,
      scene: flowSnapshot.scene,
      selectedSoundtrackId: progress.selectedSoundtrackId,
    };
  }

  public pause(): void {
    if (this.flow.getSnapshot().scene !== "gameplay") {
      return;
    }
    this.runtime.pause();
    this.notify();
  }

  public restart(): boolean {
    const mode = this.flow.getSnapshot().mode;
    if (mode === null) {
      return false;
    }
    this.runtime.pause();
    this.notify();
    if (this.exitGuard?.prepareForExit() === false) {
      return false;
    }
    this.flow.startMode(mode);
    return true;
  }

  public resume(): void {
    if (this.flow.getSnapshot().scene !== "gameplay") {
      return;
    }
    this.runtime.resume();
    this.notify();
  }

  public setNativeActive(active: boolean): boolean {
    if (this.nativeActive === active) {
      return false;
    }
    this.nativeActive = active;
    if (this.syncPlatformPause()) {
      this.notify();
    }
    return true;
  }

  public showHome(): boolean {
    const flow = this.flow.getSnapshot();
    if (flow.scene !== "gameplay") {
      return false;
    }
    this.runtime.pause();
    this.notify();
    if (this.exitGuard?.prepareForExit() === false) {
      return false;
    }
    this.flow.showHome();
    return true;
  }

  public subscribe(listener: GameplayLifecycleListener): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  public toggleMusic(): ProgressMutation<boolean> {
    return this.updatePreference("musicEnabled");
  }

  public toggleSound(): ProgressMutation<boolean> {
    return this.updatePreference("soundEnabled");
  }

  public selectSoundtrack(id: string): ProgressMutation<boolean> {
    if (!isSelectedSoundtrackId(id)) {
      return false;
    }
    const saved = this.progressStore.update((state) => ({
      ...state,
      selectedSoundtrackId: id,
    }));
    return this.finishProgressMutation(saved);
  }

  private readonly handleVisibilityChange = (): void => {
    if (this.syncPlatformPause()) {
      this.notify();
    }
  };

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.getSnapshot());
    }
  }

  private syncPlatformPause(): boolean {
    const platformPaused = this.visibilitySource.hidden || !this.nativeActive;
    if (this.runtime.getSnapshot().backgrounded === platformPaused) {
      return false;
    }
    if (platformPaused) {
      this.runtime.background();
    } else {
      this.runtime.foreground();
    }
    return true;
  }

  private updatePreference(
    preference: "musicEnabled" | "soundEnabled",
  ): ProgressMutation<boolean> {
    const saved = this.progressStore.update((state) => ({
      ...state,
      preferences: {
        ...state.preferences,
        [preference]: !state.preferences[preference],
      },
    }));
    return this.finishProgressMutation(saved);
  }

  private finishProgressMutation(
    mutation: ProgressMutation<boolean>,
  ): ProgressMutation<boolean> {
    if (!(mutation instanceof Promise)) {
      this.progressPersistence = mutation ? "durable" : "failed";
      if (mutation) {
        this.notify();
      }
      return mutation;
    }
    this.progressPersistence = "pending";
    this.notify();
    return mutation.then(
      (durable) => {
        this.progressPersistence = durable ? "durable" : "failed";
        this.notify();
        return durable;
      },
      () => {
        this.progressPersistence = "failed";
        this.notify();
        return false;
      },
    );
  }
}
